import React, { useState } from "react";
import StarRating from "./StarRating";
import { MapPin, MoreHorizontal, Share2, Flag, MessageSquare, ThumbsUp, CheckCircle2, Sparkles, ShieldCheck } from "lucide-react";

function Avatar({ name, src }) {
  if (src) return <img src={src} alt={name} className="w-10 h-10 rounded-full object-cover"/>;
  const initials = name.split(" ").map((n) => n[0]).slice(0, 2).join("");
  const colors = ["#7e57c2", "#26a69a", "#ef5350", "#42a5f5", "#66bb6a", "#ab47bc", "#ff7043"];
  const c = colors[name.length % colors.length];
  return (
    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold" style={{ backgroundColor: c }}>
      {initials}
    </div>
  );
}

export default function ReviewCard({ review }) {
  const [expanded, setExpanded] = useState(false);
  const [thanks, setThanks] = useState(review.thanks);
  const [thanked, setThanked] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const truncated = review.content.length > 380 && !expanded;
  const content = truncated ? review.content.slice(0, 380) + "…" : review.content;

  return (
    <article className="bg-white rounded-md border border-gray-200 p-4 sm:p-5 mb-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Avatar name={review.author} src={review.avatar}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <a href="#" className="text-sm font-semibold text-gray-900 hover:underline truncate">{review.author}</a>
            {review.verified && (
              <span title="Verified Reviewer" className="text-[#007a8a]">
                <ShieldCheck size={14}/>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
            {review.verified && <span>Verified Reviewer</span>}
            {review.location && (
              <>
                {review.verified && <span className="text-gray-300 hidden sm:inline">|</span>}
                <span className="inline-flex items-center gap-1 truncate"><MapPin size={11}/>{review.location}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-400 shrink-0">
          <span>#{review.id}</span>
          <button className="p-1 hover:bg-gray-100 rounded" aria-label="more"><MoreHorizontal size={16}/></button>
        </div>
      </div>

      {/* Title and meta */}
      <div className="mt-3">
        <div className="flex items-baseline gap-2 flex-wrap">
          {review.resolved && (
            <span className="inline-flex items-center gap-1 bg-[#e7f7ee] text-[#1e8e3e] text-[11px] font-semibold px-2 py-0.5 rounded">
              <CheckCircle2 size={12}/> Resolved
            </span>
          )}
        </div>
        <h3 className="text-[17px] md:text-[18px] font-bold text-gray-900 leading-snug mt-1 hover:text-[#007a8a] cursor-pointer">
          {review.title}
        </h3>
        <div className="flex items-center gap-3 text-xs text-gray-500 mt-1.5">
          <span>{review.date}</span>
          {review.rating && (
            <span className="inline-flex items-center gap-1">
              <StarRating value={review.rating} size={12}/>
              <span className="font-semibold text-gray-700">{review.rating.toFixed(1)}</span>
            </span>
          )}
        </div>
      </div>

      {/* AI Highlights */}
      {review.aiHighlights && (
        <div className="mt-3 bg-[#eef6f7] border-l-4 border-[#007a8a] rounded-r-md p-3">
          <div className="text-xs font-semibold text-[#005f6b] flex items-center gap-1 mb-1.5">
            <Sparkles size={12}/> AI Highlights
          </div>
          <ul className="space-y-1">
            {review.aiHighlights.map((h, i) => (
              <li key={i} className="text-[13px] text-gray-700 flex gap-2">
                <span className="text-[#007a8a] mt-1.5 w-1 h-1 rounded-full bg-[#007a8a] shrink-0"/>
                {h}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Content */}
      <div className="mt-3 text-[14px] text-gray-700 leading-relaxed whitespace-pre-line">
        {content}
      </div>
      {review.content.length > 380 && (
        <button onClick={() => setExpanded(!expanded)} className="text-[#007a8a] hover:underline text-sm font-medium mt-1">
          {expanded ? "Show less" : "Read more"}
        </button>
      )}

      {/* Loss */}
      {review.loss && (
        <div className="mt-3 text-sm">
          <span className="text-gray-500">Loss:</span>{" "}
          <span className="font-semibold text-[#e84e1c]">${review.loss}</span>
        </div>
      )}

      {/* Pros / Cons */}
      {(review.pros || review.cons) && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {review.pros && (
            <div>
              <div className="text-xs font-semibold text-[#1e8e3e] mb-1.5">Pros:</div>
              <ul className="space-y-1">
                {review.pros.map((p, i) => (
                  <li key={i} className="text-[13px] text-gray-700 flex gap-1.5"><span className="text-[#1e8e3e]">+</span> {p}</li>
                ))}
              </ul>
            </div>
          )}
          {review.cons && (
            <div>
              <div className="text-xs font-semibold text-[#e74c3c] mb-1.5">Cons:</div>
              <ul className="space-y-1">
                {review.cons.map((c, i) => (
                  <li key={i} className="text-[13px] text-gray-700 flex gap-1.5"><span className="text-[#e74c3c]">−</span> {c}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Preferred + recommendation */}
      {(review.preferred || review.recommendation) && (
        <div className="mt-3 space-y-1.5">
          {review.preferred && (
            <div className="text-[13px]">
              <span className="font-bold text-gray-800">Preferred solution:</span>{" "}
              <span className="text-gray-700">{review.preferred}</span>
            </div>
          )}
          {review.recommendation && (
            <div className="text-[13px]">
              <span className="font-bold text-gray-800">User's recommendation:</span>{" "}
              <span className="text-gray-700 italic">{review.recommendation}</span>
            </div>
          )}
        </div>
      )}

      <a href="#" className="inline-block text-[#007a8a] hover:underline text-sm font-medium mt-3">View full review</a>

      {/* Footer actions */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button onClick={() => setShowComments(!showComments)} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#007a8a] px-2 py-1 rounded hover:bg-gray-50">
            <MessageSquare size={15}/> Comment{review.comments?.length ? ` (${review.comments.length})` : ""}
          </button>
          <button className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#007a8a] px-2 py-1 rounded hover:bg-gray-50">
            <Share2 size={15}/> <span className="hidden sm:inline">Share Review</span>
          </button>
          <button className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#e84e1c] px-2 py-1 rounded hover:bg-gray-50">
            <Flag size={15}/> <span className="hidden sm:inline">Report</span>
          </button>
        </div>
        <button
          onClick={() => { if (!thanked) { setThanks(thanks + 1); setThanked(true); } else { setThanks(thanks - 1); setThanked(false); } }}
          className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border transition-colors ${
            thanked ? "bg-[#eef6f7] border-[#007a8a] text-[#007a8a]" : "border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}
        >
          <ThumbsUp size={14}/> <span className="font-semibold">{thanks}</span> Thank you
        </button>
      </div>

      {showComments && review.comments && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
          {review.comments.map((c) => (
            <div key={c.id} className="flex gap-3 bg-[#f9fafb] rounded-md p-3">
              <Avatar name={c.author} />
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-800">{c.author}</div>
                <div className="text-xs text-gray-500 mb-1">{c.date}</div>
                <div className="text-sm text-gray-700">{c.content}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}
